import {
  TX_FILTER_DIMENSIONS,
  rangeParamNames,
} from "@clustec/common/api-schemas";
import type { TxFilterDimensionKey } from "@clustec/common/api-schemas";
import { formatFJCompact, formatPerManaCompact } from "./format";

/**
 * Filter state for the transactions page, round-tripped through the URL so a
 * dialed-in view is shareable and survives reloads and network switches.
 * Range values stay strings end to end: URL params are strings, the server
 * compares them as ::numeric, and actualFee is a wei-scale integer that a JS
 * number would silently corrupt.
 */

export interface TxRange {
  min?: string;
  max?: string;
}

export interface TxFilterState {
  status?: string;
  feePayer?: string;
  executionResult?: string;
  hasTeardown?: "true" | "false";
  ranges: Partial<Record<TxFilterDimensionKey, TxRange>>;
}

/** Every dimension is a non-negative integer (fees are wei-scale integer strings). */
const INT_RE = /^\d+$/;

/** Whether a typed/URL range value is usable as a filter param. */
export function isValidRangeValue(value: string): boolean {
  return INT_RE.test(value);
}

export const EMPTY_TX_FILTERS: TxFilterState = { ranges: {} };

/** Read filter state out of the URL, dropping anything malformed (garbage values, min > max). */
export function parseTxFilters(params: URLSearchParams): TxFilterState {
  const state: TxFilterState = { ranges: {} };
  const status = params.get("status");
  if (status) state.status = status;
  const feePayer = params.get("feePayer");
  if (feePayer) state.feePayer = feePayer;
  const executionResult = params.get("executionResult");
  if (executionResult) state.executionResult = executionResult;
  const hasTeardown = params.get("hasTeardown");
  if (hasTeardown === "true" || hasTeardown === "false") state.hasTeardown = hasTeardown;

  for (const d of TX_FILTER_DIMENSIONS) {
    const [minParam, maxParam] = rangeParamNames(d.key);
    const minRaw = params.get(minParam);
    const maxRaw = params.get(maxParam);
    const min = minRaw !== null && INT_RE.test(minRaw) ? minRaw : undefined;
    const max = maxRaw !== null && INT_RE.test(maxRaw) ? maxRaw : undefined;
    if (min === undefined && max === undefined) continue;
    // BigInt: wei-scale fee bounds overflow Number precision.
    if (min !== undefined && max !== undefined && BigInt(min) > BigInt(max)) continue;
    state.ranges[d.key] = { min, max };
  }
  return state;
}

/**
 * Write filter state into a copy of the current params, preserving unrelated
 * ones (sort/order). Any filter change invalidates the current page, so
 * `page` is always dropped.
 */
export function applyTxFilters(base: URLSearchParams, state: TxFilterState): URLSearchParams {
  const next = new URLSearchParams(base);
  const setOrDelete = (name: string, value: string | undefined) => {
    if (value) next.set(name, value);
    else next.delete(name);
  };
  setOrDelete("status", state.status);
  setOrDelete("feePayer", state.feePayer);
  setOrDelete("executionResult", state.executionResult);
  setOrDelete("hasTeardown", state.hasTeardown);
  for (const d of TX_FILTER_DIMENSIONS) {
    const [minParam, maxParam] = rangeParamNames(d.key);
    setOrDelete(minParam, state.ranges[d.key]?.min);
    setOrDelete(maxParam, state.ranges[d.key]?.max);
  }
  next.delete("page");
  return next;
}

/** Flat query params for the list and stats endpoints. */
export function toApiQuery(state: TxFilterState): Record<string, string> {
  const query: Record<string, string> = {};
  if (state.status) query.status = state.status;
  if (state.feePayer) query.feePayer = state.feePayer;
  if (state.executionResult) query.executionResult = state.executionResult;
  if (state.hasTeardown) query.hasTeardown = state.hasTeardown;
  for (const d of TX_FILTER_DIMENSIONS) {
    const range = state.ranges[d.key];
    if (!range) continue;
    const [minParam, maxParam] = rangeParamNames(d.key);
    if (range.min !== undefined) query[minParam] = range.min;
    if (range.max !== undefined) query[maxParam] = range.max;
  }
  return query;
}

export function activeFilterCount(state: TxFilterState): number {
  let count = Object.keys(state.ranges).length;
  if (state.status) count++;
  if (state.feePayer) count++;
  if (state.executionResult) count++;
  if (state.hasTeardown) count++;
  return count;
}

/** Value formatting for a dimension, matching how TxTable renders the same column. */
export function formatDimValue(key: TxFilterDimensionKey, value: number | string): string {
  const n = Number(value);
  if (key === "actualFee") return `${formatFJCompact(n)} FJ`;
  if (key === "maxFeePerDaGas" || key === "maxFeePerL2Gas") return formatPerManaCompact(n);
  if (n >= 10_000) {
    // Counts and sizes: suffix the big ones so chip/stat text stays short.
    const tiers: Array<[number, string]> = [[1e12, "T"], [1e9, "B"], [1e6, "M"], [1e3, "K"]];
    for (const [div, suffix] of tiers) {
      if (n >= div) return `${(n / div).toFixed(1).replace(/\.0$/, "")}${suffix}`;
    }
  }
  return n.toLocaleString();
}

export interface TxFilterChip {
  /** Removal target: a dimension key, or one of the categorical param names. */
  key: TxFilterDimensionKey | "status" | "feePayer" | "executionResult" | "hasTeardown";
  label: string;
  text: string;
}

const EXECUTION_RESULT_LABELS: Record<string, string> = {
  success: "Success",
  app_logic_reverted: "App logic reverted",
  teardown_reverted: "Teardown reverted",
  both_reverted: "Both reverted",
};

export function describeFilters(state: TxFilterState): TxFilterChip[] {
  const chips: TxFilterChip[] = [];
  if (state.status) {
    chips.push({ key: "status", label: "Status", text: state.status });
  }
  if (state.executionResult) {
    chips.push({
      key: "executionResult",
      label: "Execution",
      text: EXECUTION_RESULT_LABELS[state.executionResult] ?? state.executionResult,
    });
  }
  if (state.hasTeardown) {
    chips.push({ key: "hasTeardown", label: "Teardown", text: state.hasTeardown === "true" ? "yes" : "no" });
  }
  if (state.feePayer) {
    const addr = state.feePayer;
    chips.push({
      key: "feePayer",
      label: "Fee payer",
      text: addr.length > 14 ? `${addr.slice(0, 8)}…${addr.slice(-4)}` : addr,
    });
  }
  for (const d of TX_FILTER_DIMENSIONS) {
    const range = state.ranges[d.key];
    if (!range) continue;
    const { min, max } = range;
    let text: string;
    if (min !== undefined && max !== undefined) {
      text = min === max ? formatDimValue(d.key, min) : `${formatDimValue(d.key, min)}–${formatDimValue(d.key, max)}`;
    } else if (min !== undefined) {
      text = `≥ ${formatDimValue(d.key, min)}`;
    } else {
      text = `≤ ${formatDimValue(d.key, max!)}`;
    }
    chips.push({ key: d.key, label: d.label, text });
  }
  return chips;
}

/** New state with one filter removed — what a chip's × does. */
export function clearFilter(state: TxFilterState, key: TxFilterChip["key"]): TxFilterState {
  if (key === "status" || key === "feePayer" || key === "executionResult" || key === "hasTeardown") {
    const next = { ...state, ranges: state.ranges };
    delete next[key];
    return next;
  }
  const ranges = { ...state.ranges };
  delete ranges[key];
  return { ...state, ranges };
}

/**
 * Canonical query string for a filter set — what a saved view stores. Built
 * from an empty base, so param order is fixed and two equal filter sets always
 * serialize identically ("is this already saved?" is a string compare). It
 * doubles as the /txs URL that reopens the view in the filter panel, and it
 * re-validates through parseTxFilters on the way back in.
 */
export function serializeTxFilters(state: TxFilterState): string {
  return applyTxFilters(new URLSearchParams(), state).toString();
}

/** A starting name for a new saved view: its chips, compressed. */
export function defaultViewName(state: TxFilterState): string {
  const name = describeFilters(state)
    .map((c) => `${c.label} ${c.text}`)
    .join(" · ");
  return name.length > 60 ? `${name.slice(0, 59)}…` : name;
}
